<template>
  <b-modal
    ref="modal-imei-excel"
    hide-footer
    size="lg"
    title="Xuất Excel danh sách IMEI"
    id="modal-imei-excel"
    @show="onShow"
    @shown="onShown"
    @hidden="onHidden"
  >
    <div class="imei-export-filters">
      <b-row>
        <b-col md="6">
          <label class="filter-label">Ngày nhập</label>
          <div class="d-flex align-items-center">
            <date-picker
              placeholder="Từ ngày"
              class="form-control form-control-sm"
              :config="dpConfigs.date"
              v-model="filters.fromDay"
            ></date-picker>
            <span class="mx-2 text-muted">→</span>
            <date-picker
              placeholder="Đến ngày"
              class="form-control form-control-sm"
              :config="dpConfigs.date"
              v-model="filters.toDay"
            ></date-picker>
          </div>
          <div class="mt-2">
            <b-button
              v-for="preset in DATE_PRESETS"
              :key="preset.key"
              size="sm"
              pill
              class="mr-1 mb-1 preset-chip"
              :variant="
                activePreset === preset.key ? 'primary' : 'outline-secondary'
              "
              @click="applyDatePreset(preset)"
              >{{ preset.label }}</b-button
            >
          </div>
        </b-col>
        <b-col md="3">
          <label class="filter-label">Cửa hàng</label>
          <BDropdownCustom
            :key="`stores-${dropdownKey}`"
            v-model="filters.stores"
            :options="storeOptions"
            :placeholder="'cửa hàng'"
            :matchKey="['name', 'shortName']"
            :searchable="true"
            :compareById="true"
            :limit="5"
          />
        </b-col>
        <b-col md="3">
          <label class="filter-label">Trạng thái</label>
          <BDropdownCustom
            :key="`statuses-${dropdownKey}`"
            v-model="filters.statuses"
            :options="statusOptions"
            :placeholder="'trạng thái'"
            :matchKey="['name']"
            :searchable="true"
            :limit="5"
          />
        </b-col>
      </b-row>

      <div class="more-filters mt-3">
        <div class="d-flex align-items-center justify-content-between">
          <b-link
            class="more-filters__toggle"
            @click="showMoreFilters = !showMoreFilters"
          >
            <i
              class="fas fa-chevron-right more-filters__chevron"
              :class="{ 'is-open': showMoreFilters }"
            ></i>
            Bộ lọc khác
            <span class="text-muted font-weight-normal"
              >(IMEI, sản phẩm, danh mục, NCC)</span
            >
            <b-badge v-if="moreFilterCount" pill variant="primary" class="ml-1">
              {{ moreFilterCount }}
            </b-badge>
          </b-link>
          <b-link
            v-if="moreFilterCount"
            class="text-danger small"
            @click="clearMoreFilters"
            >Xoá bộ lọc khác</b-link
          >
        </div>
        <b-collapse v-model="showMoreFilters">
          <b-row class="mt-3">
            <b-col md="6" class="mb-3">
              <label class="filter-label">Mã IMEI</label>
              <b-input
                placeholder="Nhập mã IMEI"
                v-model="filters.search"
              ></b-input>
            </b-col>
            <b-col md="6" class="mb-3">
              <label class="filter-label">Sản phẩm</label>
              <b-input
                placeholder="Nhập mã, tên sản phẩm"
                v-model="filters.searchProduct"
              ></b-input>
            </b-col>
            <b-col md="6" class="mb-3">
              <label class="filter-label">Danh mục sản phẩm</label>
              <treeselect
                :options="cateOptions"
                :multiple="true"
                :searchable="true"
                placeholder="Chọn danh mục sản phẩm"
                :match-keys="['id', 'label']"
                :clearable="true"
                :limit="1"
                openDirection="bottom"
                :append-to-body="true"
                :z-index="TREESELECT_Z_INDEX"
                v-model="filters.categories"
              />
            </b-col>
            <b-col md="6" class="mb-3">
              <label class="filter-label">Danh mục nội bộ</label>
              <treeselect
                :options="internalCateOptions"
                :multiple="true"
                :searchable="true"
                placeholder="Chọn danh mục nội bộ"
                :match-keys="['id', 'label']"
                :clearable="true"
                :limit="1"
                openDirection="bottom"
                :append-to-body="true"
                :z-index="TREESELECT_Z_INDEX"
                v-model="filters.internalCategories"
              />
            </b-col>
            <b-col md="6">
              <label class="filter-label">Nhà cung cấp</label>
              <Autosuggest
                :model="filters.providerName"
                :suggestions="filteredProviders"
                placeholder="tên nhà cung cấp"
                @select="onSelectProvider"
                @change="onChangeProvider"
              />
            </b-col>
          </b-row>
        </b-collapse>
      </div>

      <div class="d-flex align-items-center mt-4">
        <SaveButton
          class="mr-3"
          :button-style="{
            fontWeight: '600',
            minWidth: '70px',
            whiteSpace: 'nowrap',
          }"
          :is-busy="isSubmitting('export')"
          :disabled="isSubmitting('export')"
          @click="exportToExcelStream"
        >
          <v-icon small class="text-white">mdi-download</v-icon>
          <strong>Xuất Excel</strong>
        </SaveButton>
        <b-button
          style="width: 80px"
          variant="secondary"
          size="sm"
          :disabled="isSubmitting('export')"
          @click="$bvModal.hide('modal-imei-excel')"
        >
          <strong>Đóng</strong>
        </b-button>
        <b-link class="ml-3" @click="onResetToPage">Đặt lại theo trang</b-link>
      </div>
    </div>

    <div class="mt-5">
      <hr
        class="hr-text"
        data-content="Lịch sử xuất excel"
        style="font-weight: 600"
      />
      <b-table
        class="myTable"
        responsive
        bordered
        hover
        show-empty
        empty-text="Chưa có file nào"
        :fields="fieldHistory"
        :items="histories"
      >
        <template v-slot:cell(name)="row">
          <span :title="row.item.fileName" v-b-tooltip>{{
            row.item.label
          }}</span>
        </template>
        <template v-slot:cell(summary)="row">
          <span class="text-muted">{{ row.item.summary }}</span>
        </template>
        <template v-slot:cell(actions)="row">
          <div
            v-if="
              +row.item.processingStatus ===
              PROCESSING_DOWNLOAD_STATUS.COMPLETED
            "
            class="d-flex align-items-center justify-content-between"
          >
            <span class="text-success font-weight-bold">Hoàn tất</span>
            <b-button
              size="sm"
              variant="outline-primary"
              :disabled="isSubmitting('download-' + row.item.id)"
              @click="downloadExcel(row.item.id)"
            >
              <v-icon small class="text-primary">mdi-download</v-icon> Tải
            </b-button>
          </div>
          <div
            v-else-if="
              +row.item.processingStatus ===
              PROCESSING_DOWNLOAD_STATUS.PROCESSING
            "
            class="d-flex align-items-center"
          >
            <b-progress height="20px" class="flex-grow-1">
              <b-progress-bar
                :value="row.item.process"
                :label="`${row.item.process}%`"
                animated
                variant="success"
              ></b-progress-bar>
            </b-progress>
            <b-button
              size="sm"
              variant="outline-danger"
              class="ml-2"
              title="Dừng xuất file"
              :disabled="isSubmitting('cancel-' + row.item.id)"
              @click="cancelDownload(row.item.id)"
              >Huỷ</b-button
            >
          </div>
          <div v-else class="d-flex align-items-center justify-content-between">
            <span class="text-danger font-weight-bold">Lỗi</span>
            <b-button
              size="sm"
              variant="outline-secondary"
              :disabled="isSubmitting('cancel-' + row.item.id)"
              @click="removeFailedExport(row.item.id)"
              >Xoá</b-button
            >
          </div>
        </template>
      </b-table>
    </div>
  </b-modal>
</template>

<script>
import moment from 'moment';
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import datePicker from 'vue-bootstrap-datetimepicker';
import 'pc-bootstrap4-datetimepicker/build/css/bootstrap-datetimepicker.css';
import { makeToastFaile, makeToastSuccess, xoa_dau } from '@/utils/common';
import { PROCESSING_DOWNLOAD_STATUS } from '@/utils/enum';
import timeUtils, { getStartOfDay, getEndOfDay } from '@/utils/date';
import ApiService from '@/core/services/api.service';
import fileDownload from '@/utils/file-download';
import Autosuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';
import BDropdownCustom from '@/view/base/bootstrap/BDropdownCustom.vue';
import SaveButton from '@/view/components/common/SaveButton.vue';
import submitLock from '@/view/mixins/submit-lock.mixins';

const POLLING_INTERVAL_MS = 5000;
const MAX_POLL_FAILURES = 5;
const DATE_FORMAT = 'DD/MM/YYYY';
const HISTORY_TIME_FORMAT = 'DD/MM HH:mm';
const SUMMARY_DATE_FORMAT = 'DD/MM/YY';
const SUMMARY_STATUS_LIMIT = 2;
// File được ghi url ngay sau khi API trả về (và lịch sử đọc replica) nên dòng mới có thể chưa có ở lượt nạp đầu.
const HISTORY_RETRY_LIMIT = 4;
const HISTORY_RETRY_DELAY_MS = 1000;
// Treeselect append-to-body mặc định z-index 999 < modal bootstrap (1050) nên menu bị modal che.
const TREESELECT_Z_INDEX = 1060;
const DATE_PRESETS = [
  { key: '7d', label: '7 ngày', amount: 7, unit: 'days' },
  { key: '30d', label: '30 ngày', amount: 30, unit: 'days' },
  { key: '3m', label: '3 tháng', amount: 3, unit: 'months' },
  { key: '6m', label: '6 tháng', amount: 6, unit: 'months' },
  // Bỏ trống cả 2 đầu ngày = không lọc ngày nhập (xuất toàn bộ).
  { key: 'all', label: 'Tất cả', isAllTime: true },
];

const emptyFilters = () => ({
  fromDay: '',
  toDay: '',
  stores: [],
  statuses: [],
  search: '',
  searchProduct: '',
  categories: [],
  internalCategories: [],
  providerId: null,
  providerName: '',
});

export default {
  props: {
    // Bộ lọc đang áp dụng ở trang, chụp lúc mở popup; popup sửa trên bản sao.
    initialFilters: { type: Object, default: () => ({}) },
    storeOptions: { type: Array, default: () => [] },
    statusOptions: { type: Array, default: () => [] },
    cateOptions: { type: Array, default: () => [] },
    internalCateOptions: { type: Array, default: () => [] },
    providerOptions: { type: Array, default: () => [] },
  },
  mixins: [submitLock],
  components: {
    Autosuggest,
    BDropdownCustom,
    SaveButton,
    Treeselect,
    datePicker,
  },
  data() {
    return {
      PROCESSING_DOWNLOAD_STATUS,
      DATE_PRESETS,
      TREESELECT_Z_INDEX,
      dpConfigs: timeUtils.DP_CONFIG,
      filters: emptyFilters(),
      // Đổi key để dựng lại BDropdownCustom: nó chỉ nạp v-model qua watcher (không immediate, chỉ cộng thêm).
      dropdownKey: 0,
      showMoreFilters: false,
      filteredProviders: [],
      histories: [],
      pollingIntervals: {},
      pollingFailures: {},
      // Số thứ tự lượt nạp lịch sử, để bỏ kết quả của lượt cũ về sau lượt mới.
      historyRequestId: 0,
      isDestroyed: false,
      // Chỉ true khi popup đang mở: mọi lần nạp lịch sử / bật polling đến muộn sau khi đóng đều bị chặn.
      isShown: false,
      fieldHistory: [
        {
          key: 'name',
          label: 'Xuất lúc',
          thStyle: { fontWeight: 600, color: '#181c32', width: '22%' },
        },
        {
          key: 'summary',
          label: 'Bộ lọc',
          thStyle: { fontWeight: 600, color: '#181c32' },
        },
        {
          key: 'actions',
          label: 'Trạng thái',
          thStyle: { fontWeight: 600, color: '#181c32', width: '34%' },
        },
      ],
    };
  },
  computed: {
    activePreset() {
      const { fromDay, toDay } = this.filters;
      const today = moment().format(DATE_FORMAT);
      const preset = DATE_PRESETS.find((item) =>
        item.isAllTime
          ? !fromDay && !toDay
          : toDay === today &&
            fromDay ===
              moment().subtract(item.amount, item.unit).format(DATE_FORMAT),
      );
      return preset ? preset.key : null;
    },
    moreFilterCount() {
      const { search, searchProduct, categories, internalCategories } =
        this.filters;
      return [
        search && search.trim(),
        searchProduct && searchProduct.trim(),
        categories.length,
        internalCategories.length,
        this.filters.providerId,
      ].filter(Boolean).length;
    },
  },
  beforeDestroy() {
    // Cờ riêng chứ không dùng historyRequestId: nhịp polling chạy nốt sau destroy tự tăng số lượt nên guard đó không chặn được, nó dựng lại interval mà không còn ai clear.
    this.isDestroyed = true;
    this.stopPolling();
  },
  methods: {
    onShow() {
      this.resetFilters();
      // Mở sẵn "Bộ lọc khác" nếu trang đang dùng các bộ lọc trong đó, để người dùng thấy chúng.
      this.showMoreFilters = this.moreFilterCount > 0;
    },
    onShown() {
      this.isShown = true;
      // Nội dung modal chỉ được dựng sau @show, lúc filters đã có giá trị nên dropdown chưa thấy.
      this.syncDropdownSelections();
      return this.getHistoryExportExcel();
    },
    onHidden() {
      this.isShown = false;
      // Tăng số lượt để response lịch sử đang bay bị bỏ khi về tới.
      this.historyRequestId++;
      this.stopPolling();
    },
    resetFilters() {
      const filters = {
        ...emptyFilters(),
        ...JSON.parse(JSON.stringify(this.initialFilters || {})),
      };
      // Cửa hàng lấy từ URL chỉ có id: đổi sang option đầy đủ để dropdown hiện được tên.
      filters.stores = filters.stores.map(
        (store) =>
          this.storeOptions.find((option) => option.id === store.id) || store,
      );
      this.filters = filters;
      this.filteredProviders = [...this.providerOptions];
    },
    onResetToPage() {
      this.resetFilters();
      this.syncDropdownSelections();
    },
    // BDropdownCustom không tự bỏ lựa chọn cũ khi v-model đổi: dựng lại rồi gán mảng mới để nó nạp đúng.
    async syncDropdownSelections() {
      this.dropdownKey++;
      await this.$nextTick();
      this.filters.stores = [...this.filters.stores];
      this.filters.statuses = [...this.filters.statuses];
    },
    clearMoreFilters() {
      Object.assign(this.filters, {
        search: '',
        searchProduct: '',
        categories: [],
        internalCategories: [],
        providerId: null,
        providerName: '',
      });
    },
    applyDatePreset(preset) {
      if (preset.isAllTime) {
        this.filters.fromDay = '';
        this.filters.toDay = '';
        return;
      }
      this.filters.fromDay = moment()
        .subtract(preset.amount, preset.unit)
        .format(DATE_FORMAT);
      this.filters.toDay = moment().format(DATE_FORMAT);
    },
    onSelectProvider(option) {
      this.filters.providerId = option.item.id;
      this.filters.providerName = option.item.name;
    },
    onChangeProvider(text) {
      const keyword = xoa_dau(text || '').toLowerCase();
      this.filters.providerName = text || '';
      // Xoá chữ là bỏ lọc NCC, không giữ id cũ ngầm phía sau.
      if (!text) {
        this.filters.providerId = null;
      }
      this.filteredProviders = this.providerOptions.filter(
        (item) => xoa_dau(item.name).toLowerCase().indexOf(keyword) > -1,
      );
    },
    // Cùng tham số trang danh sách gửi cho API `imeis`, để file xuất khớp danh sách.
    buildParams() {
      const { filters } = this;
      return {
        status: filters.statuses.map((item) => item.code),
        search: filters.search ? filters.search.trim() : '',
        providerId: filters.providerId,
        searchProduct: filters.searchProduct,
        storeIds: filters.stores.map((store) => store.id),
        fromDate: getStartOfDay(filters.fromDay),
        toDate: getEndOfDay(filters.toDay),
        cateId: filters.categories,
        internalCateId: filters.internalCategories,
      };
    },
    exportToExcelStream() {
      return this.withSubmitLock('export', () => this.doExportToExcelStream());
    },
    async doExportToExcelStream() {
      const { fromDay, toDay } = this.filters;
      if (
        fromDay &&
        toDay &&
        moment(fromDay, DATE_FORMAT).isAfter(moment(toDay, DATE_FORMAT))
      ) {
        return makeToastFaile('Từ ngày không được lớn hơn đến ngày');
      }

      const params = this.buildParams();
      let exportedId = null;
      try {
        // Ghi file chạy nền, response chỉ báo đã nhận kèm id lịch sử. Tải lại ở bảng lịch sử bên dưới.
        const { data } = await ApiService.query(
          'productEmeiStock/export-excel',
          { params },
        );
        exportedId = data.data ? data.data.id : null;
        makeToastSuccess(data.message);
        // Chỉ áp bộ lọc của popup về trang khi đã nhận lệnh xuất, để danh sách khớp file vừa xuất.
        this.$emit('apply-filters', JSON.parse(JSON.stringify(this.filters)));
      } catch (err) {
        makeToastFaile(await this.getErrorMessage(err));
      } finally {
        this.$nprogress.done();
        // await để khoá giữ tới khi histories có dòng mới, tránh nút mở lại quá sớm.
        await this.refreshHistoryUntilFound(exportedId);
      }
    },
    async refreshHistoryUntilFound(historyId) {
      for (let attempt = 1; attempt <= HISTORY_RETRY_LIMIT; attempt++) {
        await this.getHistoryExportExcel();
        const isFound =
          !historyId || this.histories.some((item) => item.id === historyId);
        if (isFound || !this.isShown || attempt === HISTORY_RETRY_LIMIT) {
          return;
        }
        await new Promise((resolve) =>
          setTimeout(resolve, HISTORY_RETRY_DELAY_MS),
        );
      }
    },
    // Tóm tắt từ `parameters` mà API lưu lúc xuất (ReportHistory), không phải bộ lọc hiện tại.
    buildSummary(parameters) {
      const params =
        typeof parameters === 'string'
          ? JSON.parse(parameters || '{}')
          : parameters || {};
      const parts = [];
      if (params.fromDate || params.toDate) {
        const from = params.fromDate
          ? moment(params.fromDate).format(SUMMARY_DATE_FORMAT)
          : '...';
        const to = params.toDate
          ? moment(params.toDate).format(SUMMARY_DATE_FORMAT)
          : '...';
        parts.push(`${from} – ${to}`);
      } else {
        parts.push('Mọi ngày nhập');
      }
      const storeIds = params.storeIds || [];
      if (storeIds.length) {
        parts.push(`${storeIds.length} cửa hàng`);
      }
      const statusCodes = (params.status || []).map(Number);
      if (statusCodes.length) {
        const names = statusCodes.map((code) => {
          const option = this.statusOptions.find(
            (item) => Number(item.code) === code,
          );
          return option ? option.name : code;
        });
        const extra = names.length - SUMMARY_STATUS_LIMIT;
        parts.push(
          names.slice(0, SUMMARY_STATUS_LIMIT).join(', ') +
            (extra > 0 ? ` +${extra}` : ''),
        );
      }
      if (params.search) {
        parts.push(`IMEI: ${params.search}`);
      }
      if (params.hasProductSearch) {
        parts.push('Lọc sản phẩm');
      }
      if (params.providerId) {
        parts.push('Lọc NCC');
      }
      return parts.join(' · ');
    },
    async getHistoryExportExcel() {
      if (this.isDestroyed || !this.isShown) {
        return;
      }

      const requestId = ++this.historyRequestId;

      try {
        // BE chỉ trả các dòng lịch sử của IMEI đã có url.
        const { data } = await ApiService.query(
          'productEmeiStock/history-export-excel',
        );

        if (!data.status) {
          return;
        }

        const rows = await Promise.all(
          data.data.map(async (item) => {
            let process = 100;

            if (
              +item.processingStatus === PROCESSING_DOWNLOAD_STATUS.PROCESSING
            ) {
              const progress = await this.getProcessDownload(item.id);
              process = progress ? progress.process : 0;
            }

            return {
              ...item,
              fileName: item.url.split('/').pop(),
              label: moment(item.createdAt).format(HISTORY_TIME_FORMAT),
              summary: this.buildSummary(item.parameters),
              process,
            };
          }),
        );

        // Hàm này gọi từ nhiều chỗ nên hai lượt dễ chồng nhau; lượt cũ về sau sẽ ghi đè bảng bằng trạng thái cũ hơn.
        if (requestId !== this.historyRequestId) {
          return;
        }

        this.histories = rows;
        // Bật polling SAU khi chắc là lượt mới nhất, tránh lượt cũ dựng lại interval cho bản ghi đã xong.
        rows.forEach((item) => {
          if (
            +item.processingStatus === PROCESSING_DOWNLOAD_STATUS.PROCESSING
          ) {
            this.startPolling(item.id);
          }
        });
      } catch (err) {
        makeToastFaile(await this.getErrorMessage(err));
      }
    },
    downloadExcel(id) {
      return this.withSubmitLock(`download-${id}`, () =>
        this.doDownloadExcel(id),
      );
    },
    async doDownloadExcel(id) {
      try {
        const response = await ApiService.query(
          `productEmeiStock/download-excel?id=${id}`,
          { responseType: 'blob' },
        );
        fileDownload.createFile(
          response.data,
          fileDownload.getFileName(response),
        );
      } catch (err) {
        makeToastFaile(await this.getErrorMessage(err));
      } finally {
        // await để khoá giữ tới khi bảng bỏ dòng vừa tải — tải xong là BE hạ cờ downloaded và xoá file.
        await this.getHistoryExportExcel();
      }
    },
    cancelDownload(id, successMessage) {
      return this.withSubmitLock(`cancel-${id}`, () =>
        this.doCancelDownload(id, successMessage),
      );
    },
    // Dòng lỗi cũng gỡ qua API huỷ, nhưng message BE là "Dừng tiến trình..." không hợp ngữ cảnh.
    removeFailedExport(id) {
      return this.cancelDownload(id, 'Đã xoá khỏi danh sách');
    },
    async doCancelDownload(id, successMessage) {
      try {
        // ApiService.delete tự nuốt lỗi, trả về error object nên phải kiểm data && data.status, không thì toast 2 lần.
        const { data } = await ApiService.delete(
          `productEmeiStock/cancel-process-excel?id=${id}`,
        );

        if (data && data.status) {
          makeToastSuccess(successMessage || data.message);
        }
      } catch (err) {
        makeToastFaile(await this.getErrorMessage(err));
      } finally {
        this.stopPolling(id);
        // await để khoá giữ tới khi dòng đã biến khỏi bảng, không cho bấm huỷ lần hai.
        await this.getHistoryExportExcel();
      }
    },
    async getProcessDownload(id) {
      try {
        const { data } = await ApiService.get(
          `productEmeiStock/process-excel?id=${id}`,
        );

        return data.data;
      } catch (err) {
        return null;
      }
    },
    startPolling(id) {
      if (this.isDestroyed || !this.isShown || this.pollingIntervals[id]) {
        return;
      }

      this.pollingIntervals[id] = setInterval(async () => {
        const progress = await this.getProcessDownload(id);

        // Nhịp đã chờ response lúc interval bị clear (đóng popup / huỷ / dừng) thì bỏ, không ghi gì nữa.
        if (!this.pollingIntervals[id]) {
          return;
        }

        // Mất mạng là tạm thời: chịu vài nhịp hỏng rồi mới bỏ, dừng ngay nhịp đầu là dòng kẹt PROCESSING.
        if (!progress) {
          this.pollingFailures[id] = (this.pollingFailures[id] || 0) + 1;

          if (this.pollingFailures[id] < MAX_POLL_FAILURES) {
            return;
          }

          this.stopPolling(id);
          return this.getHistoryExportExcel();
        }

        this.pollingFailures[id] = 0;

        this.histories = this.histories.map((item) =>
          item.id === id ? { ...item, process: progress.process } : item,
        );

        // BE trả process 100 cho cả bản ghi ERROR nên mốc 100 đã bao luôn ca lỗi.
        if (+progress.process === 100) {
          this.stopPolling(id);
          this.getHistoryExportExcel();
        }
      }, POLLING_INTERVAL_MS);
    },
    // Không truyền id thì dọn toàn bộ - dùng khi đóng popup hoặc component bị huỷ.
    stopPolling(id) {
      const ids = id ? [id] : Object.keys(this.pollingIntervals);

      ids.forEach((key) => {
        clearInterval(this.pollingIntervals[key]);
        delete this.pollingIntervals[key];
        delete this.pollingFailures[key];
      });
    },
    async getErrorMessage(err) {
      if (!err.response) {
        return 'Không kết nối được máy chủ, vui lòng thử lại.';
      }

      const data = err.response.data;

      // Request blob thì body lỗi cũng là Blob, phải đọc text rồi parse mới ra message.
      if (data instanceof Blob) {
        try {
          const { message } = JSON.parse(await data.text());
          return message || 'Lỗi!';
        } catch (parseError) {
          return 'Lỗi!';
        }
      }

      return (data && data.message) || 'Lỗi!';
    },
  },
};
</script>

<style scoped>
.filter-label {
  font-size: 12px;
  font-weight: 600;
  color: #7e8299;
  margin-bottom: 4px;
}
.preset-chip {
  font-size: 11px;
  padding: 2px 10px;
}
.more-filters {
  background: #f9fafc;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  padding: 10px 14px;
}
.more-filters__toggle {
  font-weight: 600;
  color: #3f4254;
}
.more-filters__toggle:hover {
  color: #3699ff;
  text-decoration: none;
}
.more-filters__chevron {
  font-size: 10px;
  margin-right: 6px;
  transition: transform 0.15s ease;
}
.more-filters__chevron.is-open {
  transform: rotate(90deg);
}
/* 3 loại ô (b-input, treeselect, Autosuggest) mặc định cao/font khác nhau: ép cùng 36px, 13px để thẳng hàng. */
.more-filters ::v-deep .form-control,
.more-filters ::v-deep .autosuggest__input {
  height: 36px;
  font-size: 13px;
  padding: 0 12px;
  border-radius: 4px;
}
.more-filters ::v-deep .autosuggest__input::placeholder {
  color: #b5b5c3;
}
.more-filters ::v-deep .vue-treeselect__control {
  height: 36px;
  border-radius: 4px;
  font-size: 13px;
}
.more-filters ::v-deep .vue-treeselect__placeholder,
.more-filters ::v-deep .vue-treeselect__single-value {
  line-height: 34px;
}
.more-filters ::v-deep .vue-treeselect__multi-value {
  margin-bottom: 0;
}
</style>
